// Copyright 2017 Sorama B.V.

import { autoinject, bindable, BindingEngine, computedFrom, customElement, Disposable } from "aurelia-framework";
import { MeasurementHealthIssue, MeasurementHealthIssueType } from "datatypes/SoramaLocalApi";
import { Chameleon } from "libraries/Chameleon/Chameleon";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ConnectedHardwareService, HardwareStatusInfo } from "services/Hardware/ConnectedHardwareService";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";

@customElement("hardware-status")
@autoinject
export class HardwareStatus {
    private measurementAcquisitionService: IMeasurementAcquisitionService;
    private bindingEngine: BindingEngine;
    private connectedHardwareService: ConnectedHardwareService;
    private shellViewManager: ShellViewManager;

    private alertType: string;
    private alertText: string;
    private alertSubText: string;
    private alertIcon: string;
    private alertLoading: boolean;
    private secondaryIcon: string;

    @computedFrom("connectedHardwareService.hardwareStatus", "connectedHardwareService.webcamStatus")
    private get isWebcamDisconnected(): boolean {
        return (
            this.connectedHardwareService.hardwareStatus === HardwareStatusInfo.Connected &&
            this.connectedHardwareService.webcamStatus !== HardwareStatusInfo.Connected
        );
    }

    @computedFrom("measurementAcquisitionService.issues")
    private get displayedIssues(): MeasurementHealthIssue[] {
        return this.measurementAcquisitionService.issues.filter((issue) =>
            this.ignoreIssues.find((ignoreIssueType) => issue.type == ignoreIssueType) ? false : true
        );
    }

    private disposables: Disposable[];

    @bindable
    public clickable = false;

    @bindable
    public ignoreIssues: MeasurementHealthIssueType[] = [];

    constructor(
        measurementAcquisitionService: IMeasurementAcquisitionService,
        bindingEngine: BindingEngine,
        shellViewManager: ShellViewManager,
        connectedHardwareService: ConnectedHardwareService
    ) {
        this.measurementAcquisitionService = measurementAcquisitionService;
        this.bindingEngine = bindingEngine;
        this.connectedHardwareService = connectedHardwareService;
        this.shellViewManager = shellViewManager;
    }

    public bind(): void {
        this.disposables = [
            this.bindingEngine
                .propertyObserver(this.connectedHardwareService, "hardwareStatus")
                .subscribe((newValue) => this.updateAlert(newValue, this.connectedHardwareService.isRefreshing)),
            this.bindingEngine
                .propertyObserver(this.connectedHardwareService, "isRefreshing")
                .subscribe((newValue) => this.updateAlert(this.connectedHardwareService.hardwareStatus, newValue)),
        ];

        this.updateAlert(this.connectedHardwareService.hardwareStatus, this.connectedHardwareService.isRefreshing);

        // If the module appears, try to connect to hardware.
        this.connectedHardwareService.tryConnect();
    }

    public unbind(): void {
        while (this.disposables.length > 0) this.disposables.pop().dispose();
    }

    private updateAlert(status: HardwareStatusInfo, isRefreshing: boolean): void {
        if (status == HardwareStatusInfo.Connected) {
            this.alertType = "alert-success";
            this.alertSubText = this.clickable ? "Click here to change the device" : "";
            this.alertText = (this.measurementAcquisitionService.activeSetup ? "Sound Camera is c" : "C") + "onnected";
            this.alertIcon = "fa-check";
            this.alertLoading = false;
            Chameleon.setObservable("camera", true);
        } else if (status == HardwareStatusInfo.Connecting) {
            this.alertType = "alert-info";
            this.alertText = "Connecting to device, please stand-by";
            this.alertSubText = "This can take up to one minute";
            this.alertLoading = true;
            this.alertIcon = "";
            Chameleon.setObservable("camera", false);
        } else if (isRefreshing) {
            this.alertType = "alert-info";
            this.alertText = "Discovering devices, please stand-by";
            this.alertSubText = "This can take up to one minute";
            this.alertLoading = true;
            this.alertIcon = "";
            Chameleon.setObservable("camera", false);
        } else if (status == HardwareStatusInfo.NotConnected) {
            const reason = this.measurementAcquisitionService.isReserved.reason;
            this.alertType = "alert-warning";
            this.alertText = "Connection failed, retrying";
            this.alertLoading = false;
            this.alertIcon = "fa-exclamation-triangle";
            this.alertSubText = reason ? reason : "Sound Camera is not connected or in use";
            Chameleon.setObservable("camera", false);
        } else if (status == HardwareStatusInfo.NoDevice) {
            // Show nothing in this case
            this.alertType = "alert-info";
            this.alertText = "No devices found";
            this.alertSubText = "";
            this.alertIcon = "";
            this.alertLoading = false;
            this.secondaryIcon = "";
            Chameleon.setObservable("camera", false);
        } else {
            this.alertType = "alert-warning";
            this.alertText = "No Sound Camera selected";
            this.alertIcon = "fa-exclamation-triangle";
            this.alertLoading = false;
            this.alertSubText = this.clickable ? "Click here to change the device" : "";
            Chameleon.setObservable("camera", false);
        }

        Chameleon.checkObservable("camera");
    }
}
